'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ArrowLeft, Box, ChevronRight, Folder, Grid2x2Plus, Music, Plus, Play, Settings } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import { cn } from '@/lib/utils';
import { detectPlatform, formatShortcut } from '@/lib/workspace/commands';
import { useAuth } from '@/components/auth-provider';
import { CreatePostForm, CreateChannelForm } from '@/components/create/create-forms';
import {
  PANEL_CREATE_BADGE,
  PanelSearchInput,
  panelChip,
} from '@/components/workspace/panels/panel-ui';
import { ChannelPanelView } from '@/components/marketplace/channel-panel-view';
import { PostDetailView } from '@/components/posts/post-detail-view';
import { MarketplaceFeed } from '@/components/marketplace-feed';
import { MyContentManager } from '@/components/workspace/panels/my-content-manager';
import { usePanelBackStack } from '@/components/workspace/panels/use-panel-back';
import { kindVisual } from '@/components/workspace/panels/content-preview';
import { resolveContentKind } from '@/lib/content/app-identity';
import { resolveYoursBand, useFirstRunDone } from '@/lib/workspaces/first-run';
import { contentOpenRanks, recordContentOpen } from '@/lib/workspaces/recent-opens';
import { coverGradient } from '@/lib/marketplace/cover';
import { useWorkspaceActions } from '@/components/workspace/store';
import { useWorkspaceCardActions } from '@/components/workspace/card-actions-context';
import { MyFilesSummary } from '@/components/workspace/my-files-summary';
import { AppsTab } from '@/components/workspace/panels/apps-tab';
import { DraftAppPanel } from '@/components/marketplace/draft-app-panel';
import {
  FileContextMenu,
  useFileContextMenu,
  type ContextMenuItem,
} from '@/components/workspace/panels/file-context-menu';
import { openCreateWithDialog } from '@/components/workspace/create-with-dialog';
import { resolveCardImageSrc } from '@/components/discover/post-discover-card';
import { CATALOG_GRID_CLS, CatalogCard, type CatalogListing } from './catalog-card';
import { isMineShelfLoading, mineAgentName } from './mine-shelf';
import { StarterBand } from './starter-band';
import {
  DISCOVER_PREFETCH_URLS,
  DISCOVER_RECENT_URL,
  MINE_PAGE,
  cachedJson,
  mineShelfUrl,
  peekJson,
} from './discover-data';
import {
  DISCOVER_FOCUS_EVENT,
  DISCOVER_LEVEL_EVENT,
  consumeInitialLevel,
  consumeInitialRoot,
  type DiscoverLevel,
} from './discover-events';

/**
 * Discover — ONE surface where apps, agents and your own work live, rendered
 * in two mounts:
 *
 *  - `overlay`: full width over the canvas area (the rail stays visible; this
 *    is never the fullscreen takeover). You pressed Discover, which is
 *    explicit intent to find something, so the field autofocuses and the
 *    catalog carries the weight.
 *  - `inline`: centered on an empty screen (including the ground state and
 *    the anon shell). You did not ask to type, so no autofocus — and there is
 *    NOT ONE `openWorkspacePanel` call on this surface. The canvas mounts for
 *    a null user while the panel host does not, so a single panel event here
 *    recreates the dead-affordance bug on the most-linked pages in the
 *    product. Keep it that way.
 *
 * Band order is fixed: field, then YOURS (starters on first run, Recent
 * after), then a named divider, then the catalog. The user's own things never
 * share a band with things we want them to install.
 *
 * The detail stack carries three kinds of level — a listing/post detail, the
 * content manager (My files), and the feed (See more) — all through the same
 * Back/Escape/history mechanics. Anything that wants a rail button should be
 * asked whether it is really just a level.
 */

/**
 * The catalog ROOT. An agent is one kind of app, so this narrows WITHIN the
 * Apps band rather than switching bands. New and Mine are separate booleans,
 * not members of this union: they are modifiers that compose with the root
 * (Agents + Mine = my agents), which is what the chips promise by sitting in
 * the same row.
 */
type Root = 'all' | 'apps' | 'agents';

type CategoryChip = { root: 'apps' | 'agents'; slug: string; name: string };

type RecentRow = {
  id: string;
  displayName: string;
  contentType: string;
  mimeType: string;
  metadata?: Record<string, unknown> | null;
  updatedAt?: string;
};

type CommunityPost = {
  id: string;
  title: string | null;
  thumbnailUrl: string | null;
  mediaUrl: string | null;
  mediaType: string | null;
  totalLikes?: number | null;
  commentCount: number | null;
  author: { username: string; avatarUrl: string | null } | null;
};

const PAGE = 24;

/**
 * D2 ("fit the screenful") constants. The overlay's default catalog is
 * COMPUTED to end at the fold: columns are read from the rendered grid
 * (browser truth, no drift with the CSS), rows from the height left
 * between the grid's top and the scroll container's bottom. The card
 * height is a constant because the diet made cards uniform (measured 169
 * on a live desktop card; 170 keeps the floor division honest).
 */
const CARD_H = 170;
const GRID_GAP = 16;
/** Space reserved under the grid for the See-more row. */
const SEE_MORE_H = 52;
/**
 * Breathing room under the last band, so content ends ABOVE the pinned
 * footer's rule instead of touching it. Reserved in the row plan too (see
 * `avail`), or the grid would size itself into space the padding occupies
 * and put the surface into a short scroll it is designed not to need.
 */
const CONTENT_PAD_B = 16;
const MIN_ROWS = 2;
const MAX_ROWS = 4;
/** Community column: one post row ≈ thumb 56px + gap. */
const POST_ROW_H = 64;

export function DiscoverView({
  variant,
  onClose,
}: {
  variant: 'overlay' | 'inline';
  onClose?: () => void;
}) {
  const t = useT();
  const { user } = useAuth();
  const { addItem } = useWorkspaceActions();
  const cardActions = useWorkspaceCardActions();

  // ---- search --------------------------------------------------------------
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);
  // Rail press while the inline mount is showing: focus the field instead of
  // opening a second Discover over it.
  useEffect(() => {
    if (variant !== 'inline') return;
    const onFocus = () => inputRef.current?.focus();
    window.addEventListener(DISCOVER_FOCUS_EVENT, onFocus);
    return () => window.removeEventListener(DISCOVER_FOCUS_EVENT, onFocus);
  }, [variant]);

  // ---- detail stack --------------------------------------------------------
  const [stack, setStack] = useState<DiscoverLevel[]>(() => {
    const initial = variant === 'overlay' ? consumeInitialLevel() : null;
    return initial ? [initial] : [];
  });
  const push = useCallback((level: DiscoverLevel) => {
    setStack((s) => [...s, level]);
  }, []);
  // A deep-link level arriving while this overlay is LIVE (an in-app /p/
  // /c/ click, a late URL action): the mount-time `consumeInitialLevel`
  // has already run, so `openDiscover(level)` delivers by event instead.
  useEffect(() => {
    if (variant !== 'overlay') return;
    const onLevel = (e: Event) => {
      const level = (e as CustomEvent<DiscoverLevel>).detail;
      if (level) push(level);
    };
    window.addEventListener(DISCOVER_LEVEL_EVENT, onLevel);
    return () => window.removeEventListener(DISCOVER_LEVEL_EVENT, onLevel);
  }, [variant, push]);
  const pop = useCallback(() => setStack((s) => s.slice(0, -1)), []);
  // The feed's inner tab, mirrored up so the level header's "+ Create"
  // composes the matching thing: post on Activity, channel on Channels.
  const [feedTab, setFeedTab] = useState<'activity' | 'channels'>('activity');
  // Each pushed level is a real history entry. Channel and post levels stamp
  // their PUBLIC path onto the entry, so the state is linkable and a reload
  // lands on the canonical page; Back restores the workspace URL because the
  // previous entry carries it. Files/feed have no public path and stay quiet.
  const top = stack[stack.length - 1] ?? null;
  usePanelBackStack(
    stack.length,
    pop,
    top?.kind === 'channel' ? `/c/${top.slug}` : top?.kind === 'post' ? `/p/${top.id}` : null,
  );

  // Escape is layered: pop a level; with the stack empty, close the overlay.
  // The canvas's own Escape (Stage exit) respects defaultPrevented, so a
  // handled press here never leaks through.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
        // Esc in a FILLED field clears the query first; the next Esc
        // escalates (pop level / close), matching field conventions.
        if (target.tagName === 'INPUT' && (target as HTMLInputElement).value) {
          e.preventDefault();
          setQuery('');
          return;
        }
      }
      if (stack.length > 0) {
        e.preventDefault();
        pop();
      } else if (variant === 'overlay' && onClose) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stack.length, pop, variant, onClose]);

  // ---- catalog -------------------------------------------------------------
  // Apps lead (user call). The chip row is TWO axes that read as one row:
  // Apps / Agents pick the root (always exactly one), New / Mine are toggles
  // layered over it. `all` is not reachable from the chrome but stays in the
  // union for deep links and the mixed grid. A fresh overlay can be asked to
  // start on Agents (the empty workspace's "Find an agent", `openDiscoverOn`);
  // read once, at mount, the same way the initial level is.
  const [root, setRoot] = useState<Root>(
    () => (variant === 'overlay' ? consumeInitialRoot() : null) ?? 'apps',
  );
  const [newOnly, setNewOnly] = useState(false);
  const [mineOnly, setMineOnly] = useState(false);
  const [category, setCategory] = useState<CategoryChip | null>(null);
  const [catalogExpanded, setCatalogExpanded] = useState(false);
  // Any filter switch re-collapses: each browse starts calm.
  useEffect(() => {
    setCatalogExpanded(false);
  }, [root, newOnly, mineOnly, category]);
  // Seed from the module cache (warmed by the shell prefetch): a reopen —
  // and, after the idle prefetch, even the first open — paints the full
  // catalog on the first frame instead of popping bands in one by one.
  const [apps, setApps] = useState<CatalogListing[]>(
    () => (peekJson<{ items?: CatalogListing[] }>(DISCOVER_PREFETCH_URLS[0])?.items ?? []),
  );
  const [agents, setAgents] = useState<CatalogListing[]>(
    () => (peekJson<{ items?: CatalogListing[] }>(DISCOVER_PREFETCH_URLS[1])?.items ?? []),
  );
  const [categories, setCategories] = useState<CategoryChip[]>([]);
  const [hasMore, setHasMore] = useState<{ apps: boolean; agents: boolean; mine: boolean }>({
    apps: false,
    agents: false,
    mine: false,
  });
  const [loadingMore, setLoadingMore] = useState(false);
  /**
   * The maker's own apps, as ordinary catalog listings. Fetched only when Mine
   * is on: it is the one shelf that isn't the marketplace, so it can't come
   * from the channels route. Unpublished ones have no channel, and carry
   * `unpublished` so the card badges them.
   *
   * `mineShelfUrl` carries the one property that makes this a shelf of APPS
   * rather than of storage rows (see its note). The query rides along, so
   * typing narrows the WHOLE shelf server-side rather than only the rows
   * already loaded.
   */
  const [mineApps, setMineApps] = useState<CatalogListing[] | null>(null);
  /**
   * The maker's own AGENTS, same shelf, other root. Mine composes with the
   * root chip, so Agents + Mine has to have somewhere to read from: the
   * marketplace channels only carry LISTED agents, and a private agent has no
   * listing at all. `/api/agents` returns the caller's own agents (owned +
   * sub-agents) in one shot — the route's default limit is its ceiling (500),
   * well above the per-plan agent cap, so this shelf never pages.
   */
  const [mineAgents, setMineAgents] = useState<CatalogListing[] | null>(null);
  /** Last shelf page that landed; `loadMore` asks for the one after it. */
  const minePage = useRef(1);
  const fetchMine = useCallback(
    async (page: number): Promise<{ items: CatalogListing[]; hasMore: boolean }> => {
      const [content, listed] = await Promise.all([
        cachedJson<{ items?: Record<string, unknown>[]; total?: number }>(
          mineShelfUrl(page, debouncedQuery),
          60_000,
        ),
        cachedJson<{ listings?: Record<string, { slug: string; verified: boolean }> }>(
          '/api/marketplace/my-listings',
          60_000,
        ),
      ]);
      const map = listed?.listings ?? {};
      const items: CatalogListing[] = (content?.items ?? []).map((row) => {
        const id = String(row.id);
        const meta = (row.metadata ?? null) as Record<string, unknown> | null;
        const hit = map[id];
        return {
          id,
          name: (row.displayName as string) || 'Untitled app',
          slug: hit?.slug ?? '',
          description: (meta?.description as string) ?? null,
          // Owner branding (the draft Manage drawer) first; the capture
          // thumbnail stays the fallback art.
          logoUrl:
            (typeof meta?.logoUrl === 'string' && (meta.logoUrl as string)) ||
            (row.thumbnailPath ? `/api/content/${id}/preview` : null),
          bannerUrl: (typeof meta?.bannerUrl === 'string' && (meta.bannerUrl as string)) || null,
          color: (typeof meta?.color === 'string' && (meta.color as string)) || null,
          verificationStatus: hit?.verified ? 'verified' : null,
          primaryContentId: id,
          primaryAgentId: null,
          widgetMetadata: meta,
          widgetPostId: null,
          installCount: null,
          createdAt: (row.createdAt as string) ?? null,
          // No "by @me" line, the whole shelf is theirs.
          makerUsername: null,
          makerDisplayName: null,
          agentUsername: null,
          agentDisplayName: null,
          agentAvatarUrl: null,
          root: 'apps' as const,
          category: '',
          unpublished: !hit,
        };
      });
      return { items, hasMore: page * MINE_PAGE < (content?.total ?? 0) };
    },
    [debouncedQuery],
  );
  /**
   * The maker's own agents as catalog listings. Filtered client-side by the
   * query: unlike the app shelf there is no server-side `q` here, and the
   * whole list arrives in one page anyway, so narrowing it locally is both
   * correct and cheaper than a round trip per keystroke.
   */
  const fetchMineAgents = useCallback(async (): Promise<CatalogListing[]> => {
    const data = await cachedJson<{ agents?: Record<string, unknown>[] }>(
      '/api/agents',
      60_000,
    );
    const q = debouncedQuery.trim().toLowerCase();
    return (data?.agents ?? [])
      .map((row): CatalogListing => {
        const username = String(row.username ?? '');
        // `/api/agents` sends `displayName`, never `name` (see mine-shelf.ts).
        const name = mineAgentName(row);
        return {
          id: String(row.id),
          name,
          slug: '',
          // The agent blurb lives on the user row as `bio`, not `description`
          // (that is the CreateAgentData field name, mapped on write).
          description: (row.bio as string) ?? null,
          logoUrl: (row.avatarUrl as string) ?? null,
          bannerUrl: null,
          color: null,
          verificationStatus: null,
          primaryContentId: null,
          primaryAgentId: String(row.id),
          widgetMetadata: null,
          widgetPostId: null,
          installCount: null,
          createdAt: (row.createdAt as string) ?? null,
          // No "by @me" line, the whole shelf is theirs.
          makerUsername: null,
          makerDisplayName: null,
          agentUsername: username,
          agentDisplayName: name,
          agentAvatarUrl: (row.avatarUrl as string) ?? null,
          // `/api/agents` returns every user column but the vectors, so the
          // TrustFlow magnitude rides along for the card's badge.
          agentScalarReputation: (row.scalarReputation as string | number | null | undefined) ?? null,
          root: 'agents' as const,
          category: '',
          // Published means shown in Discover (2026-09-25,
          // lib/agents/listing.ts): public AND not unlisted by its owner. The
          // list route computes it as `listed`; an older response without it
          // falls back to `isPublic`, the old rule. Same state the app shelf
          // badges as unpublished.
          unpublished: !((row.listed as boolean | undefined) ?? (row.isPublic as boolean | undefined)),
        };
      })
      .filter(
        (a) =>
          !q ||
          a.name.toLowerCase().includes(q) ||
          (a.agentUsername ?? '').toLowerCase().includes(q) ||
          (a.description ?? '').toLowerCase().includes(q),
      );
  }, [debouncedQuery]);

  // First page, re-run when the query changes. Deliberately NOT gated on the
  // shelf already being set: a new query has to refetch, and inside the module
  // cache's TTL the repeat trip for an unchanged query is free. Mine composes
  // with the root, so only the shelves the root can show are fetched.
  useEffect(() => {
    if (!mineOnly) return;
    let alive = true;
    if (root !== 'agents') {
      void fetchMine(1).then((r) => {
        if (!alive) return;
        minePage.current = 1;
        setMineApps(r.items);
        setHasMore((h) => ({ ...h, mine: r.hasMore }));
      });
    }
    if (root !== 'apps') {
      void fetchMineAgents().then((items) => {
        if (alive) setMineAgents(items);
      });
    }
    return () => {
      alive = false;
    };
  }, [mineOnly, root, fetchMine, fetchMineAgents]);

  const fetchRoot = useCallback(
    async (
      root: 'apps' | 'agents',
      offset: number,
      sort?: 'new',
    ): Promise<{ items: CatalogListing[]; hasMore: boolean; categories: CategoryChip[] }> => {
      const params = new URLSearchParams({ root, limit: String(PAGE), offset: String(offset) });
      if (sort) params.set('sort', sort);
      if (debouncedQuery) params.set('q', debouncedQuery);
      if (category && category.root === root) params.set('category', category.slug);
      // First pages ride the module cache (warm from the shell prefetch, so
      // a reopen paints complete on the first frame); deeper pages fetch
      // plainly — they append and are not part of the first paint.
      const url = `/api/marketplace/channels?${params.toString()}`;
      const data =
        offset === 0
          ? await cachedJson<Record<string, unknown>>(url)
          : await fetch(url).then((r) => (r.ok ? r.json() : null)).catch(() => null);
      if (!data) return { items: [], hasMore: false, categories: [] };
      const items = ((data.items ?? []) as CatalogListing[]);
      const cats = ((data.categories ?? []) as { slug: string; name: string }[]).map((c) => ({
        root,
        // The chip filter param is the slug MINUS the root prefix.
        slug: c.slug.startsWith(`${root}/`) ? c.slug.slice(root.length + 1) : c.slug,
        name: c.name,
      }));
      return { items, hasMore: !!data.hasMore, categories: cats };
    },
    [debouncedQuery, category],
  );

  // True while a first page is in flight, so an empty grid reads as
  // "searching" rather than "nothing matches" for the round trip.
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    let alive = true;
    // A picked category narrows to its root; All and New fetch both corpora.
    // New browses the SAME curated tree as every other chip — the listing
    // channels, bound + listed — just sorted by mint date. It must never
    // fall back to the raw posts corpus (dev probes, duplicate release
    // posts, no covers: content curation applies everywhere on Discover).
    if (mineOnly) return;
    const sort = newOnly ? ('new' as const) : undefined;
    const wantApps = root !== 'agents';
    const wantAgents = root !== 'apps';
    setSearching(true);
    Promise.all([
      wantApps ? fetchRoot('apps', 0, sort) : Promise.resolve(null),
      wantAgents ? fetchRoot('agents', 0, sort) : Promise.resolve(null),
    ]).then(([a, g]) => {
      if (!alive) return;
      if (a) setApps(a.items);
      if (g) setAgents(g.items);
      setHasMore((h) => ({ ...h, apps: a?.hasMore ?? false, agents: g?.hasMore ?? false }));
      setSearching(false);
    }).catch(() => {
      if (alive) setSearching(false);
    });
    return () => {
      alive = false;
    };
  }, [mineOnly, newOnly, root, fetchRoot]);

  // Categories are seeded ONCE from both roots' unfiltered first pages
  // (already warm from the shell prefetch) and never change again: the chip
  // row is a stable CONTROL. Reading it from each narrowed fetch made the
  // row reshuffle on every click — a filter that rearranges itself reads
  // as broken. Dedupe by name across roots; first root claims the chip.
  useEffect(() => {
    let alive = true;
    void Promise.all([
      cachedJson<Record<string, unknown>>(DISCOVER_PREFETCH_URLS[0]),
      cachedJson<Record<string, unknown>>(DISCOVER_PREFETCH_URLS[1]),
    ]).then(([a, g]) => {
      if (!alive) return;
      const read = (d: Record<string, unknown> | null, root: 'apps' | 'agents') =>
        ((d?.categories ?? []) as { slug: string; name: string }[]).map((c) => ({
          root,
          slug: c.slug.startsWith(`${root}/`) ? c.slug.slice(root.length + 1) : c.slug,
          name: c.name,
        }));
      // Stored UN-deduped, both roots in full: the root chip scopes
      // the category row (Apps → //apps subchannels only, Agents → //agents),
      // so a name shared across roots must survive for BOTH scopes. The
      // cross-root dedupe happens at render time, for All only.
      const cats = [...read(a, 'apps'), ...read(g, 'agents')];
      if (cats.length) setCategories(cats);
    });
    return () => {
      alive = false;
    };
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      // The maker's own app shelf pages against /api/content, not the
      // channels. The agent shelf arrives whole, so there is nothing to page.
      if (mineOnly) {
        if (root === 'agents' || !hasMore.mine) return;
        const next = minePage.current + 1;
        const r = await fetchMine(next);
        minePage.current = next;
        setMineApps((prev) => [...(prev ?? []), ...r.items]);
        setHasMore((h) => ({ ...h, mine: r.hasMore }));
        return;
      }
      const jobs: Promise<void>[] = [];
      const sort = newOnly ? ('new' as const) : undefined;
      if (root !== 'agents' && hasMore.apps) {
        jobs.push(
          fetchRoot('apps', apps.length, sort).then((r) => {
            setApps((prev) => [...prev, ...r.items]);
            setHasMore((h) => ({ ...h, apps: r.hasMore }));
          }),
        );
      }
      if (root !== 'apps' && hasMore.agents) {
        jobs.push(
          fetchRoot('agents', agents.length, sort).then((r) => {
            setAgents((prev) => [...prev, ...r.items]);
            setHasMore((h) => ({ ...h, agents: r.hasMore }));
          }),
        );
      }
      await Promise.all(jobs);
    } finally {
      setLoadingMore(false);
    }
  }, [mineOnly, newOnly, root, hasMore, apps.length, agents.length, fetchRoot, fetchMine, loadingMore]);

  // The grid is picked on both axes: Mine swaps the SOURCE (own shelves vs
  // the marketplace), the root picks which of the two sources to read, and
  // New only changes the order. All interleaves so both server rankings
  // survive instead of dumping every app before the first agent.
  const listings = useMemo(() => {
    const byNewest = (rows: CatalogListing[]) =>
      [...rows].sort((x, y) =>
        String(y.createdAt ?? '').localeCompare(String(x.createdAt ?? '')),
      );
    const interleave = (a: CatalogListing[], g: CatalogListing[]) => {
      const out: CatalogListing[] = [];
      const n = Math.max(a.length, g.length);
      for (let i = 0; i < n; i++) {
        if (a[i]) out.push(a[i]);
        if (g[i]) out.push(g[i]);
      }
      return out;
    };
    const [a, g] = mineOnly
      ? [mineApps ?? [], mineAgents ?? []]
      : [apps, agents];
    if (root === 'apps') return newOnly ? byNewest(a) : a;
    if (root === 'agents') return newOnly ? byNewest(g) : g;
    // Both roots already arrive newest-first under sort=new, so the merge
    // only has to be date-ordered rather than re-sorting each side.
    return newOnly ? byNewest([...a, ...g]) : interleave(a, g);
  }, [mineOnly, newOnly, root, apps, agents, mineApps, mineAgents]);

  // D2: measure the screenful. Columns from the RENDERED grid template
  // (computed style — always agrees with the CSS); rows from the height
  // between the grid's top and the scroll container's bottom. Re-measured
  // by ResizeObserver on the scroller AND the content wrapper (the wrapper
  // grows when Recent pops in, which moves the grid's top without resizing
  // the grid itself).
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const gridBoxRef = useRef<HTMLDivElement | null>(null);
  const [plan, setPlan] = useState<{ cols: number; rows: number }>({ cols: 4, rows: MIN_ROWS });
  useEffect(() => {
    if (variant !== 'overlay') return;
    const measure = () => {
      const grid = gridBoxRef.current;
      const scroller = scrollRef.current;
      if (!grid || !scroller) return;
      const cols = Math.max(
        1,
        getComputedStyle(grid).gridTemplateColumns.split(' ').length,
      );
      const avail =
        scroller.getBoundingClientRect().bottom -
        grid.getBoundingClientRect().top -
        SEE_MORE_H -
        CONTENT_PAD_B;
      const rows = Math.max(
        MIN_ROWS,
        Math.min(MAX_ROWS, Math.floor((avail + GRID_GAP) / (CARD_H + GRID_GAP))),
      );
      setPlan((p) => (p.cols === cols && p.rows === rows ? p : { cols, rows }));
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (scrollRef.current) ro.observe(scrollRef.current);
    if (contentRef.current) ro.observe(contentRef.current);
    return () => ro.disconnect();
  }, [variant]);

  // The screenful cap applies to the overlay's default browse only: typing a
  // query or picking a category is explicit intent to see everything that
  // matches, so those are never capped. Inline (the empty-screen mount) keeps
  // the full wall — there the catalog IS the content.
  // The category chips follow the root: Apps shows only //apps subchannels,
  // Agents only //agents. A chip for a category the current root can't
  // contain is a filter that can only produce an empty grid. All shows the
  // union, deduped by name with apps claiming the chip (its click narrows to
  // the chip's root, same as before).
  const visibleCategories = useMemo(() => {
    // Mine is not a marketplace tree — its chips would filter nothing.
    if (mineOnly) return [];
    if (root === 'apps') return categories.filter((c) => c.root === 'apps');
    if (root === 'agents') return categories.filter((c) => c.root === 'agents');
    const seen = new Set<string>();
    return categories.filter((c) => {
      const key = c.name.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [categories, mineOnly, root]);

  const cap = plan.cols * plan.rows;
  const capActive =
    variant === 'overlay' &&
    !catalogExpanded &&
    !debouncedQuery &&
    !category &&
    // Picking Mine is explicit intent to see your own shelf, exactly like
    // picking a category, so the screenful cap never applies to it. It pages
    // itself instead, through the same sentinel (see `autoLoad`).
    !mineOnly;
  const visibleListings = capActive ? listings.slice(0, cap) : listings;
  const mineLoading = isMineShelfLoading({ mineOnly, root, mineApps, mineAgents });
  const capHidesMore =
    capActive && (listings.length > cap || hasMore.apps || hasMore.agents);
  // The community column follows the catalog's computed height so the two
  // columns bottom-align — the balance fix, structural not cosmetic.
  const communityCount = Math.max(
    4,
    Math.min(12, Math.floor((plan.rows * (CARD_H + GRID_GAP)) / POST_ROW_H)),
  );

  // Decision C1: once the cap is lifted (See more, a query, a category), the
  // overlay auto-appends pages via a sentinel — button first, then infinite.
  // Safe against the classic footer objection because the My files footer is
  // pinned OUTSIDE the scroll container. The inline mount keeps the manual
  // More button: it is a bounded card on the canvas, not a browse session.
  const autoLoad =
    variant === 'overlay' &&
    !capActive &&
    (mineOnly
      ? root !== 'agents' && hasMore.mine
      : hasMore.apps || hasMore.agents);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!autoLoad) return;
    const el = sentinelRef.current;
    if (!el) return;
    // `loadMore` is recreated when a page lands (it closes over the offsets),
    // so this effect re-arms after every append — which also covers the
    // sentinel-still-visible case: the fresh observer fires immediately.
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) void loadMore();
      },
      { rootMargin: '600px 0px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
    // stack.length: while a detail level is open the sentinel is unmounted;
    // popping back must re-arm the observer on the fresh element.
  }, [autoLoad, loadMore, stack.length]);

  // ---- yours ---------------------------------------------------------------
  // Read through the hook, never `isFirstRunDone` in render: the SSR'd inline
  // mount must hydrate against what the server painted (F-019 side finding,
  // 2026-09-12; the rule is stated in lib/workspaces/rail-flag.ts).
  const firstRunDone = useFirstRunDone(user?.id);
  const RECENT_URL = DISCOVER_RECENT_URL;
  const [recent, setRecent] = useState<RecentRow[] | null>(() => {
    const d = peekJson<{ items?: RecentRow[] }>(RECENT_URL);
    return d?.items
      ? d.items.filter((r) => resolveContentKind(r).kind !== 'workspace')
      : null;
  });
  useEffect(() => {
    // Fetched for every signed-in user, latch or no latch: band 2 decides its
    // CONTENT from these rows now (F-019, 2026-09-12), so skipping the fetch
    // on first run would hide a second-device user's files behind starters.
    if (!user) return;
    let alive = true;
    void cachedJson<{ items?: RecentRow[] }>(RECENT_URL, 30_000)
      .then((d) => {
        if (!alive || !d) return;
        // Places are not things: workspaces are addressed by Stage and the
        // taskbar, and putting them here would make Discover the place you
        // switch containers (the exact drift the design rejected). The
        // content listing returns them as rows, so filter by resolved kind.
        const rows = ((d.items ?? []) as RecentRow[]).filter(
          (r) => resolveContentKind(r).kind !== 'workspace',
        );
        setRecent(rows);
      });
    return () => {
      alive = false;
    };
  }, [user, RECENT_URL]);

  const filteredRecent = useMemo(() => {
    if (!recent) return null;
    // HONEST ordering: the server sort is `updatedAt`, which is "recently
    // modified by ANYTHING" — agents, E2E runs, background writes — so rows
    // the user never touched outranked the doc they opened an hour ago.
    // Rank rows the user actually OPENED (per-device log) first, newest
    // open first; the rest keep the modified order behind them.
    const ranks = contentOpenRanks();
    const rows = [...recent].sort((a, b) => {
      const ra = ranks.get(a.id);
      const rb = ranks.get(b.id);
      if (ra !== undefined && rb !== undefined) return ra - rb;
      if (ra !== undefined) return -1;
      if (rb !== undefined) return 1;
      return 0; // keep the server's modified order for never-opened rows
    });
    if (!debouncedQuery) return rows;
    const q = debouncedQuery.toLowerCase();
    return rows.filter((r) => r.displayName.toLowerCase().includes(q));
  }, [recent, debouncedQuery]);

  const openRecent = useCallback(
    (row: RecentRow) => {
      // One window per click: a content item on the current screen. The
      // resolve-ref round trip fills the cache (identity, openWith, size).
      recordContentOpen(row.id);
      void addItem({ type: 'content', refId: row.id }).catch(() => {});
      onClose?.();
    },
    [addItem, onClose],
  );

  // ---- community -----------------------------------------------------------
  const [community, setCommunity] = useState<CommunityPost[]>(() => {
    const d = peekJson<{ posts?: CommunityPost[]; items?: CommunityPost[] }>(DISCOVER_PREFETCH_URLS[3]);
    return ((d?.posts ?? d?.items ?? []) as CommunityPost[]).slice(0, 12);
  });
  useEffect(() => {
    if (variant !== 'overlay') return;
    let alive = true;
    void cachedJson<{ posts?: CommunityPost[]; items?: CommunityPost[] }>(DISCOVER_PREFETCH_URLS[3]).then((d) => {
      if (!alive || !d) return;
      setCommunity(((d.posts ?? d.items ?? []) as CommunityPost[]).slice(0, 12));
    });
    return () => {
      alive = false;
    };
  }, [variant]);

  // ---- footer (overlay): your library, pinned bottom-left -----------------
  // Mirrors Stage's bottom-left corner (same rule: the left edge is YOURS).
  // Long TTL — quota moves slowly and this must not add a visible fetch.
  const [storage, setStorage] = useState<{ used: number; quota: number } | null>(null);
  useEffect(() => {
    if (variant !== 'overlay' || !user) return;
    let alive = true;
    void cachedJson<{ storageUsed?: number; storageQuota?: number }>('/api/user', 300_000).then(
      (d) => {
        if (!alive || !d?.storageQuota) return;
        setStorage({ used: d.storageUsed ?? 0, quota: d.storageQuota });
      },
    );
    return () => {
      alive = false;
    };
  }, [variant, user]);

  // ---- actions -------------------------------------------------------------
  const useListing = useCallback(
    (l: CatalogListing) => {
      if (!cardActions) return;
      if (l.primaryAgentId && l.agentUsername) {
        void cardActions.addAgent({
          agentId: l.primaryAgentId,
          username: l.agentUsername,
          displayName: l.agentDisplayName,
          avatarUrl: l.agentAvatarUrl,
        });
      } else if (l.primaryContentId) {
        void cardActions.addWidget({
          contentId: l.primaryContentId,
          title: l.name,
          thumbnailUrl: l.logoUrl,
          metadata: l.widgetMetadata,
          sourceWidgetPostId: l.widgetPostId,
        });
      }
      onClose?.();
    },
    [cardActions, onClose],
  );

  const openListing = useCallback(
    (l: CatalogListing) => {
      if (!l.slug) {
        // The maker's own unpublished APP gets its manage page (Publish /
        // Share), the draft's equivalent of the published channel screen —
        // launching it from a card click hid the whole manage surface.
        if (l.unpublished && l.primaryContentId && !l.primaryAgentId) {
          push({ kind: 'appDraft', listing: l });
          return;
        }
        // Anything else without a channel (agents on the Mine shelf): the
        // card opens the thing itself.
        useListing(l);
        return;
      }
      push({ kind: 'channel', slug: l.slug, name: l.name });
    },
    [push, useListing],
  );

  // Right-click / long-press menu on the maker's OWN cards (the Mine shelf,
  // plus drafts anywhere): Manage jumps straight at the owner surface — the
  // draft's identity drawer, or the published app's channel page.
  const cardCtx = useFileContextMenu<CatalogListing>();
  const cardMenuItems = useCallback(
    (l: CatalogListing): ContextMenuItem[] => {
      const items: ContextMenuItem[] = [];
      if (!l.primaryAgentId) {
        items.push({
          id: 'manage',
          label: t('user.manage'),
          icon: <Settings className="h-4 w-4" />,
          onSelect: () => {
            if (l.slug) push({ kind: 'channel', slug: l.slug, name: l.name });
            else push({ kind: 'appDraft', listing: l, manage: true });
          },
        });
      }
      items.push({
        id: 'open',
        label: l.primaryAgentId ? t('workspace.canvas.node.chat') : t('posts.open'),
        icon: <Grid2x2Plus className="h-4 w-4" />,
        onSelect: () => useListing(l),
      });
      return items;
    },
    [push, t, useListing],
  );

  // ---- stack levels --------------------------------------------------------
  const level = top;
  if (level) {
    const title =
      level.kind === 'files'
        ? 'Your files'
        : level.kind === 'apps'
          ? 'Your apps'
        : level.kind === 'feed'
          ? 'Community'
          : level.kind === 'compose'
            ? level.what === 'channel'
              ? t('discoverView.newChannel')
              : t('discoverView.newPost')
            : level.kind === 'channel'
              ? level.name
              : level.kind === 'appDraft'
                ? level.listing.name
                : 'Post';
    return (
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex shrink-0 items-center gap-2 border-b border-border/60 px-3 py-2">
          <button
            type="button"
            onClick={pop}
            aria-label={t('user.back')}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <span className="truncate text-sm font-medium">{title}</span>
          {/* D3: with Feed off the rail, this is the create path. Follows
              the feed's inner tab like the old panel header did: Activity
              composes a post, Channels a channel. */}
          {level.kind === 'feed' && !!user && (
            <button
              type="button"
              onClick={() =>
                push({
                  kind: 'compose',
                  what: feedTab === 'channels' ? 'channel' : 'post',
                })
              }
              aria-label={feedTab === 'channels' ? t('discoverView.newChannel') : t('discoverView.newPost')}
              className={cn(PANEL_CREATE_BADGE, 'ml-auto')}
            >
              <Plus className="h-3.5 w-3.5" />
              Create
            </button>
          )}
        </div>
        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
          {level.kind === 'channel' && (
            // Reading column, not a full-bleed wall: the level opens over a
            // canvas-wide overlay, and a 1900px post stream is unreadable.
            <div className="mx-auto w-full max-w-3xl px-4 pt-2">
              <ChannelPanelView
                slug={level.slug}
                name={level.name}
                canPost={!!user}
                onOpen={(slug, name) => push({ kind: 'channel', slug, name })}
                onDeleted={pop}
              />
            </div>
          )}
          {level.kind === 'post' && (
            <div className="mx-auto w-full max-w-3xl px-4 pb-4 pt-2 @container">
              <PostDetailView
                key={level.id}
                postId={level.id}
                embedded
                onBack={pop}
                onOpenPost={(id) => push({ kind: 'post', id })}
              />
            </div>
          )}
          {level.kind === 'apps' && (
            // The maker's shelf — created + remixed apps, published state per
            // card, drafts with their Publish CTA. Same frame as My files.
            <div className="mx-auto w-full max-w-6xl px-4 pb-4 xl:max-w-[1400px]">
              <AppsTab />
            </div>
          )}
          {level.kind === 'appDraft' && (
            // The draft's manage page — same reading column as a channel
            // level, so a private app and a published one land on screens of
            // the same shape.
            <div className="mx-auto w-full max-w-3xl px-4 pb-6 pt-3">
              <DraftAppPanel
                contentId={level.listing.primaryContentId ?? level.listing.id}
                name={level.listing.name}
                description={level.listing.description}
                metadata={level.listing.widgetMetadata}
                autoOpenManage={level.manage}
                actions={
                  <button
                    type="button"
                    onClick={() => useListing(level.listing)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium transition-colors hover:bg-accent"
                  >
                    <Grid2x2Plus className="h-4 w-4" />
                    {t('posts.open')}
                  </button>
                }
              />
            </div>
          )}
          {level.kind === 'files' && (
            // Same frame as the surface (and its footer): the level reads as
            // the same page, not a different app. h-full is LOAD-BEARING: the
            // manager is its own scroller (`h-full` + inner overflow-y-auto)
            // and pins its quota footer; in a plain block wrapper that h-full
            // resolves to auto, the inner scroller grows to content height,
            // and its infinite-scroll sentinel then ALWAYS intersects its
            // root — the panel silently fetched page after page forever.
            <div className="mx-auto h-full w-full max-w-6xl xl:max-w-[1400px]">
              <MyContentManager />
            </div>
          )}
          {level.kind === 'feed' && (
            // Two-pane, like X (decision F2): reading column + the trending
            // rail (agents + channels to follow). The rail lives INSIDE
            // MarketplaceFeed behind a @3xl/main container query — the panel
            // mount never provided the container context, which is why the
            // panel always collapses to a single column. Here we provide it,
            // so the rail appears at ≥48rem of level width and the level
            // degrades to the reading column below that for free.
            <div className="@container/main mx-auto w-full max-w-5xl px-4 pb-6">
              <MarketplaceFeed
                panelTabs
                initialTab="activity"
                onOpenPost={(id) => push({ kind: 'post', id })}
                onTabChange={(tab) =>
                  setFeedTab(tab === 'channels' ? 'channels' : 'activity')
                }
              />
            </div>
          )}
          {level.kind === 'compose' && (
            <div className="mx-auto w-full max-w-2xl px-4 pb-6 pt-3">
              {level.what === 'channel' ? (
                <CreateChannelForm onSuccess={pop} />
              ) : (
                <CreatePostForm onSuccess={pop} />
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- the surface ---------------------------------------------------------
  // Wireframe density: the overlay caps content width and uses larger card
  // minimums (four to five calm columns), instead of an edge-to-edge wall.
  // D1 calibration: 250px card floor (research sweet spot lands cards at
  // 270-320), 16px gaps (12 is the visual-merge floor). Phones get one
  // column (see CATALOG_GRID_CLS). The overlay's grid is shared with the
  // public /discover page, so both draw the catalog at the same density.
  const gridCls =
    variant === 'overlay'
      ? CATALOG_GRID_CLS
      : 'grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(150px,1fr))]';
  // Recent is a SHORTCUT row, not a gallery (D1): the overlay renders one
  // scrolling line of icon+name chips; the inline card keeps its small grid.
  const recentGridCls =
    variant === 'overlay'
      ? 'flex gap-2 overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden [mask-image:linear-gradient(to_right,black,black_calc(100%_-_2.5rem),transparent)]'
      : 'grid gap-2.5 [grid-template-columns:repeat(auto-fill,minmax(160px,1fr))]';
  const bandHead = (label: string) => (
    <div className="mb-2.5 flex min-h-[30px] items-center gap-2.5">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="h-px flex-1 bg-border/70" />
    </div>
  );

  // Band 2 is DATA-driven (F-019, 2026-09-12): the decision reads the
  // UNFILTERED rows, so typing a query that matches none of them does not
  // swap the starters in mid-search; the filtered list only decides whether
  // the Recent band has anything to draw. The head names whatever it covers.
  const hasRecent = !!user && !!recent && recent.length > 0;
  const yoursBand = resolveYoursBand({ hasRecent });
  const yoursHead = bandHead(
    yoursBand.head === 'recent' ? t('discoverView.recent') : t('discoverView.getStarted'),
  );
  // The inline mount has no pinned footer, so its files entry lives in this
  // band; the overlay's footer owns it there. Hidden before the first spawn
  // when there is nothing to list yet.
  const showInlineFilesLink = variant === 'inline' && !!user && (hasRecent || firstRunDone);
  // The column is a VARIANT decision, not a data decision: deciding it by
  // `community.length` re-laid-out the whole catalog when the fetch landed
  // (the grid snapped from full width to two columns). Reserved always on
  // the overlay; posts fill in, and while loading it holds quiet stubs.
  const showCommunity = variant === 'overlay';

  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-col',
        variant === 'overlay' ? 'px-4 pb-4 pt-3 md:px-5 md:pt-4 xl:px-12' : 'px-1 pb-1 pt-1',
      )}
    >
      {/* The field lives INSIDE the same width cap as the content — a field
          spanning the full overlay while the catalog started 300px in read
          as two unrelated surfaces. The chord hint rides the field chip:
          it is exactly where the eye is when the surface is open, and the
          rail tooltip already covers hover. */}
      <div className={cn(variant === 'overlay' && 'mx-auto w-full max-w-6xl xl:max-w-[1400px]')}>
        <PanelSearchInput
          value={query}
          onChange={setQuery}
          placeholder={t('discoverView.searchPlaceholder')}
          autoFocus={variant === 'overlay'}
          inputRef={inputRef}
          hint={
            variant === 'overlay'
              ? formatShortcut({ key: 'k', mod: true, shift: true }, detectPlatform())
              : undefined
          }
        />
      </div>

      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
        {/* A CONTAINER, not a viewport: Shrink makes this column 26rem while
            the viewport stays desktop, so anything sized with `md:` below
            would lay out for a screen the content no longer occupies. */}
        <div
          ref={contentRef}
          className={cn(
            '@container/discover',
            // D1 calibration inside D2: 1152 up to xl (the 1100-1200
            // standard), then a 1400 cap with 48px gutters from the root's
            // xl:px-12 - gutters grow past ~1600 instead of the content.
            variant === 'overlay' && 'mx-auto w-full max-w-6xl pb-4 xl:max-w-[1400px]',
          )}
        >
        {/* Band 2, YOURS. Recent when it has rows, the starters otherwise;
            never both, never empty. Until 2026-09-12 the latch chose the
            CONTENT, and an account with nothing of its own got an empty slot
            on the overlay after its first app launch (F-019 in
            docs/internal/handoffs/2026-09-12-hx-qa-dashboard-verification.md).
            Overlay margins are the section rhythm (D2 balance pass): the
            bands breathe apart instead of reading as one dense block. */}
        {yoursBand.content === 'starters' ? (
          // Reserved height: the band renders null until starters resolve,
          // and an unreserved slot made the divider jump up then down. The
          // reservation matches the CHIP-ROW anatomy (head + one chip line),
          // the same footprint the Recent band settles into later.
          <div
            className={cn(
              'min-h-[4.75rem]',
              variant === 'overlay' ? 'mb-10 mt-2' : 'mb-4 mt-1',
            )}
          >
            {yoursHead}
            <StarterBand />
            {showInlineFilesLink && (
              <div className="mt-1.5 flex justify-end">
                <button
                  type="button"
                  onClick={() => push({ kind: 'files' })}
                  className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground"
                >
                  <Folder className="h-3 w-3" />
                  {t('discoverView.seeMyFiles')}
                  <ChevronRight className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
        ) : filteredRecent && filteredRecent.length > 0 ? (
          <div className={cn(variant === 'overlay' ? 'mb-10 mt-2' : 'mb-4 mt-1')}>
            {yoursHead}
            <div className={recentGridCls}>
              {filteredRecent.slice(0, variant === 'overlay' ? 10 : 4).map((row) => {
                const kind = resolveContentKind(row);
                const { Icon, tone } = kindVisual(kind);
                return variant === 'overlay' ? (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => openRecent(row)}
                    title={`${row.displayName} · ${kind.label}`}
                    className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-border/60 bg-background py-1.5 pl-1.5 pr-3 text-left transition-colors hover:bg-accent/40 focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    <span className={cn('inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md', tone)}>
                      <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
                    </span>
                    <span className="max-w-[11rem] truncate text-xs font-medium text-foreground">
                      {row.displayName}
                    </span>
                  </button>
                ) : (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => openRecent(row)}
                    className="flex items-center gap-2.5 rounded-xl border border-border/60 bg-background p-2.5 text-left transition-colors hover:bg-accent/40 focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    <span className={cn('inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', tone)}>
                      <Icon className="h-4 w-4" strokeWidth={1.8} />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-xs font-medium text-foreground">{row.displayName}</span>
                      <span className="block truncate text-[10px] text-muted-foreground">{kind.label}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            {/* On the overlay the pinned footer owns the files entry; the
                inline mount has no footer, so the band keeps its link. */}
            {showInlineFilesLink && (
              <div className="mt-1.5 flex justify-end">
                <button
                  type="button"
                  onClick={() => push({ kind: 'files' })}
                  className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground"
                >
                  {t('discoverView.seeMyFiles')}
                  <ChevronRight className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
        ) : (
          // Rows exist but the query filtered every one of them out: the
          // search results below own the surface, exactly as before. Only a
          // NON-empty filter can reach here, so the no-query slot is never
          // blank.
          null
        )}

        {/* Band 3 — the two band heads share ONE line: the catalog's head
            (with Create at its right) in the left column, the
            community's head at the top of its column. In Shrink and on
            phones the community column stacks under the catalog instead of
            being crushed into a 100px track beside it. */}
        <div className={cn(showCommunity && 'grid gap-10 @2xl/discover:grid-cols-[minmax(0,1fr)_280px] @2xl/discover:gap-12')}>
          <div className="min-w-0">
            <div className="mb-2.5 flex min-h-[30px] items-center gap-2.5">
              {/* The band names what the grid shows: the root chip below
                  picks Apps or Agents, and the head follows it. It read
                  "Apps" whatever the chip, so Agents showed a grid of agents
                  under "APPS" (portal visual pass, 2026-09-28). The segment
                  that used to sit here is gone; the space it freed carries
                  Create. */}
              <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground" data-testid="discover-band-head">
                {root === 'agents' ? t('discover.agents') : t('discover.widgets')}
              </span>
              <span className="h-px flex-1 bg-border/70" />
              {/* The only make-verb in the chrome. It follows the root the
                  same way the feed level's Create follows its inner tab, so
                  the button never has to ask what you meant. */}
              <button
                type="button"
                onClick={() => openCreateWithDialog(root === 'agents' ? 'agent' : 'app')}
                aria-label={t('actions.create')}
                title={t('actions.create')}
                className={cn(PANEL_CREATE_BADGE, 'shrink-0')}
              >
                <Plus className="h-3.5 w-3.5" />
                {t('actions.create')}
              </button>
            </div>

            {/* Chips: New + the STABLE category set, one scrolling row
                behind an edge fade at every width. */}
            <div
              className="mb-5 flex items-center gap-1.5 overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden [mask-image:linear-gradient(to_right,black,black_calc(100%_-_2.5rem),transparent)]"
              // Inline: a global panel rule sets `scrollbar-width: thin` and
              // outranks the utility class - the bar rendered anyway.
              style={{ scrollbarWidth: 'none' }}
            >
              {/* The root, first in the row. A radio pair, not toggles:
                  exactly one is always on, so New / Mine / a category always
                  has something to narrow. */}
              {([
                ['apps', t('discover.widgets')],
                ['agents', t('discover.agents')],
              ] as const).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={root === value}
                  className={panelChip(root === value)}
                  onClick={() => {
                    setRoot(value);
                    setCategory(null);
                  }}
                >
                  {label}
                </button>
              ))}
              {/* Modifiers. They compose with the root and with each other:
                  Agents + Mine + New is my newest agents. Turning one off
                  leaves the root exactly where it was. */}
              <button
                type="button"
                aria-pressed={newOnly}
                className={panelChip(newOnly)}
                onClick={() => setNewOnly((v) => !v)}
              >
                {t('discover.sortNew')}
              </button>
              <button
                type="button"
                aria-pressed={mineOnly}
                className={panelChip(mineOnly)}
                onClick={() => {
                  // Your own shelf is not the marketplace tree, so a category
                  // picked over there cannot survive the switch.
                  setMineOnly((v) => !v);
                  setCategory(null);
                }}
              >
                {t('discover.scopeMine')}
              </button>
              {visibleCategories.map((c) => (
                <button
                  key={`${c.root}/${c.slug}`}
                  type="button"
                  className={panelChip(category?.slug === c.slug && category?.root === c.root)}
                  onClick={() => {
                    // Toggle: a second click clears the chip. The root stays
                    // where the chip put it — it is visibly selected in this
                    // same row, so nothing is left narrowed invisibly.
                    const off = category?.slug === c.slug && category?.root === c.root;
                    setCategory(off ? null : c);
                    if (!off) setRoot(c.root);
                  }}
                >
                  {c.name}
                </button>
              ))}
              {/* Scrolled to the end, the last chip must clear the 2.5rem
                  fade (it sat under it for good); padding would not reliably
                  extend a flex scroller's end. */}
              <span aria-hidden className="w-9 shrink-0" />
            </div>
            <>
                <div
                  ref={gridBoxRef}
                  className={cn(gridCls, visibleListings.length === 0 && 'min-h-[14rem]')}
                >
                  {visibleListings.length === 0 && (searching || mineLoading) && (
                    // The round trip, in the same footprint as the empty
                    // state below. Mine used to skip this: its shelves start
                    // as `null`, the grid read that as an empty list, and
                    // "No results found" showed until the fetch landed.
                    <div
                      role="status"
                      className="col-span-full flex min-h-[14rem] items-center justify-center px-4 text-center text-sm text-muted-foreground"
                    >
                      {t('discoverView.loading')}
                    </div>
                  )}
                  {visibleListings.length === 0 && !searching && !mineLoading && (
                    // A query or filter that matches nothing used to leave a
                    // silent blank band; say so, in the grid's own footprint.
                    <div className="col-span-full flex min-h-[14rem] items-center justify-center px-4 text-center text-sm text-muted-foreground">
                      {debouncedQuery
                        ? `${t('discover.noResults')} "${debouncedQuery}"`
                        : t('discover.noSearchResults')}
                    </div>
                  )}
                  {visibleListings.map((l) => (
                    // `display: contents` wrapper: carries the context-menu
                    // triggers without disturbing the grid (the card stays
                    // the grid item). Own cards only — the Mine shelf, plus
                    // drafts (which only ever appear there).
                    <div
                      key={l.id}
                      className="contents"
                      {...(mineOnly || l.unpublished ? cardCtx.bindTrigger(l) : {})}
                    >
                      <CatalogCard
                        listing={l}
                        onOpenListing={openListing}
                        onUse={useListing}
                      />
                    </div>
                  ))}
                </div>
                <FileContextMenu
                  anchor={cardCtx.anchor}
                  onClose={cardCtx.close}
                  items={cardCtx.target ? cardMenuItems(cardCtx.target) : []}
                />
                {capHidesMore ? (
                  <div className="mt-3 flex justify-center">
                    <button
                      type="button"
                      onClick={() => setCatalogExpanded(true)}
                      className="inline-flex items-center gap-1 rounded-md border border-border/60 px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-accent"
                    >
                      {t('discoverView.seeMore')}
                      <ChevronRight className="h-3 w-3" />
                    </button>
                  </div>
                ) : autoLoad ? (
                  // The sentinel row doubles as the quiet loading indicator;
                  // no button — scrolling is the request now.
                  <div
                    ref={sentinelRef}
                    className="flex h-10 items-center justify-center"
                    aria-hidden
                  >
                    {loadingMore && (
                      <span className="text-[11px] text-muted-foreground">{t('discoverView.loading')}</span>
                    )}
                  </div>
                ) : (
                  (mineOnly
                    ? root !== 'agents' && hasMore.mine
                    : hasMore.apps || hasMore.agents) && (
                    <div className="mt-3 flex justify-center">
                      <button
                        type="button"
                        disabled={loadingMore}
                        onClick={() => void loadMore()}
                        className="inline-flex items-center gap-1 rounded-md border border-border/60 px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-accent disabled:opacity-60"
                      >
                        {loadingMore ? t('discoverView.loading') : t('discoverView.more')}
                      </button>
                    </div>
                  )
                )}
            </>
          </div>

          {/* Community column — bounded rows; the endless version is one
              level deeper, where infinite scroll is right because you asked
              for it. */}
          {showCommunity && (
            <div className="min-w-0">
              {bandHead(t('marketplace.community'))}
              <div className="flex flex-col gap-3">
                {community.length > 0
                  ? community.slice(0, communityCount).map((p) => {
                      // The SHARED tile-media chain (post-discover-card):
                      // thumbnail → derived video poster → image-shaped
                      // mediaUrl. Video posts with none of those render a
                      // muted first-frame <video> instead of the old blank
                      // gradient; every video gets the play glyph.
                      const thumb = resolveCardImageSrc(p);
                      const isVideo = p.mediaType === 'video';
                      const likes = p.totalLikes ?? 0;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => push({ kind: 'post', id: p.id })}
                          className="flex items-start gap-2.5 text-left"
                        >
                          <span
                            className="relative h-14 w-24 shrink-0 overflow-hidden rounded-lg"
                            // Gradient UNDERLAY always: a poster that 404s
                            // (previews are best-effort) hides itself via
                            // onError and this shows through — never the
                            // broken-image glyph.
                            style={{ background: coverGradient(p.id).css }}
                          >
                            {thumb ? (
                              // eslint-disable-next-line @next/next/no-img-element
                              <img
                                src={thumb}
                                alt=""
                                loading="lazy"
                                className="h-full w-full object-cover"
                                onError={(e) => {
                                  (e.currentTarget as HTMLElement).style.display =
                                    'none';
                                }}
                              />
                            ) : isVideo && p.mediaUrl ? (
                              <video
                                src={p.mediaUrl}
                                muted
                                playsInline
                                preload="metadata"
                                className="h-full w-full object-cover"
                              />
                            ) : null}
                            {p.mediaType === '3d' && (
                              <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-black/55">
                                  <Box className="h-2.5 w-2.5 text-white" />
                                </span>
                              </span>
                            )}
                            {p.mediaType === 'audio' && (
                              <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-black/55">
                                  <Music className="h-2.5 w-2.5 text-white" />
                                </span>
                              </span>
                            )}
                            {isVideo && (
                              <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-black/55">
                                  <Play className="ml-0.5 h-2.5 w-2.5 fill-white text-white" />
                                </span>
                              </span>
                            )}
                          </span>
                          <span className="min-w-0">
                            <span className="line-clamp-2 text-xs font-medium leading-snug text-foreground">
                              {p.title ?? 'Untitled'}
                            </span>
                            <span className="mt-0.5 block truncate text-[10px] text-muted-foreground">
                              {p.author?.username ? `@${p.author.username}` : 'unknown'}
                              {likes > 0 ? ` · ${likes} likes` : ''}
                              {typeof p.commentCount === 'number' && p.commentCount > 0
                                ? ` · ${p.commentCount} replies`
                                : ''}
                            </span>
                          </span>
                        </button>
                      );
                    })
                  : // Quiet stubs hold the column's shape while the fetch is
                    // in flight, so the catalog never re-lays-out.
                    Array.from({ length: communityCount }, (_, i) => (
                      <div key={i} className="flex items-start gap-2.5" aria-hidden>
                        <span className="h-14 w-24 shrink-0 rounded-lg bg-muted/60" />
                        <span className="min-w-0 flex-1 space-y-1.5 pt-0.5">
                          <span className="block h-2.5 w-4/5 rounded bg-muted/60" />
                          <span className="block h-2 w-2/5 rounded bg-muted/50" />
                        </span>
                      </div>
                    ))}
              </div>
              <div className="mt-2.5 flex justify-end">
                <button
                  type="button"
                  onClick={() => push({ kind: 'feed' })}
                  className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground"
                >
                  {t('discoverView.seeMore')}
                  <ChevronRight className="h-3 w-3" />
                </button>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Pinned footer — the overlay's bottom-left is YOURS, same corner rule
          as Stage. With the catalog capped at two rows this is visible
          without scrolling, which is the point: your library has a fixed
          address on the surface, not a link that drifts with content. */}
      {variant === 'overlay' && user && (
        <div className="mx-auto flex w-full max-w-6xl shrink-0 items-center border-t border-border/60 pt-2.5 xl:max-w-[1400px]">
          <button
            type="button"
            onClick={() => push({ kind: 'files' })}
            data-testid="discover-my-files"
            className="inline-flex min-w-0 items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-accent/60"
          >
            {/* One face for both mounts — Stage's corner chip renders the
                same MyFilesSummary composition. */}
            <MyFilesSummary storage={storage} />
          </button>
          {/* The maker's shelf sits beside the library: created + remixed
              apps, drafts included — the answer to "where are my
              unpublished apps" has a fixed address too. */}
        </div>
      )}
    </div>
  );
}
